/*
 * Клиент control-plane API (§1.2: фронт — клиент API, без торговой логики).
 *
 * - Токен живёт ТОЛЬКО в памяти (передаётся сюда стором; не пишем в localStorage —
 *   §1.12 «в браузере ничего не кешируется»).
 * - Деньги/ставки — строки (не трогаем их как числа здесь).
 * - Индикатор активности: счётчик in-flight запросов с подпиской (шапка мигает).
 */

import type {
  EngineState,
  ParserSnapshot,
  CommandKind,
  BotConfigDto,
  ClosedTrade,
} from './types'

export const API_BASE = (import.meta.env.VITE_API_BASE ?? '/api/v1').replace(/\/$/, '')

/** Корень сервера (без префикса `/api/v1`) — для `/healthz`, который вне версии API. */
const API_ROOT = API_BASE.replace(/\/api\/v\d+$/, '')

/** Ошибка API с HTTP-статусом и человекочитаемым detail. */
export class ApiError extends Error {
  readonly status: number
  readonly detail: string
  constructor(status: number, detail: string) {
    super(`HTTP ${status}: ${detail}`)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

// --- Токен (в памяти) -------------------------------------------------------
let authToken: string | null = null
export function setAuthToken(token: string | null): void {
  authToken = token
}
export function getAuthToken(): string | null {
  return authToken
}

// --- Индикатор активности (in-flight) --------------------------------------
let inFlight = 0
const activityListeners = new Set<(busy: boolean) => void>()

function emitActivity(): void {
  const busy = inFlight > 0
  for (const cb of activityListeners) cb(busy)
}

export function subscribeActivity(cb: (busy: boolean) => void): () => void {
  activityListeners.add(cb)
  return () => activityListeners.delete(cb)
}

function beginRequest(): void {
  inFlight += 1
  emitActivity()
}

function endRequest(): void {
  inFlight = Math.max(0, inFlight - 1)
  emitActivity()
}

// --- Ядро запроса -----------------------------------------------------------
interface RequestOptions {
  method?: 'GET' | 'POST'
  body?: unknown
  /** Запрос без токена (только /healthz). */
  anonymous?: boolean
  /** Базовый URL вместо `/api/v1` (для `/healthz` под корнем сервера). */
  base?: string
  signal?: AbortSignal
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, anonymous = false, base = API_BASE, signal } = opts
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (!anonymous) {
    if (!authToken) throw new ApiError(401, 'нет токена')
    headers.Authorization = `Bearer ${authToken}`
  }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  beginRequest()
  let resp: Response
  try {
    resp = await fetch(`${base}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: signal ?? null,
      // Контрол-плейн на том же origin (прокси/nginx); без credentials —
      // токен идёт заголовком, не куками.
      credentials: 'omit',
    })
  } catch (err) {
    endRequest()
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw new ApiError(0, 'сеть недоступна (движок/API не отвечает)')
  }
  endRequest()

  if (!resp.ok) {
    throw new ApiError(resp.status, await extractDetail(resp))
  }
  if (resp.status === 204) return undefined as T
  return (await resp.json()) as T
}

async function extractDetail(resp: Response): Promise<string> {
  try {
    const data = (await resp.json()) as { detail?: unknown }
    if (typeof data.detail === 'string') return data.detail
    if (data.detail != null) return JSON.stringify(data.detail)
  } catch {
    /* не-JSON тело — отдаём статус-текст */
  }
  return resp.statusText || `ошибка ${resp.status}`
}

// --- Эндпойнты --------------------------------------------------------------
export interface HealthResponse {
  status: string
  protocol_version: number
}

export function getHealth(signal?: AbortSignal): Promise<HealthResponse> {
  return request<HealthResponse>('/healthz', { anonymous: true, base: API_ROOT, signal })
}

export function getStatus(signal?: AbortSignal): Promise<EngineState> {
  return request<EngineState>('/status', { signal })
}

export function getParser(signal?: AbortSignal): Promise<ParserSnapshot> {
  return request<ParserSnapshot>('/parser', { signal })
}

export interface ExchangesResponse {
  exchanges: string[]
}

export function getExchanges(signal?: AbortSignal): Promise<ExchangesResponse> {
  return request<ExchangesResponse>('/exchanges', { signal })
}

/** Текущий конфиг бота (форма настроек §1.11). Значения — как на границе (строки/числа). */
export function getConfig(signal?: AbortSignal): Promise<BotConfigDto> {
  return request<BotConfigDto>('/config', { signal })
}

export interface HistoryResponse {
  records: ClosedTrade[]
}

/** Хвост истории закрытых связок (новейшие первыми). */
export function getHistory(limit = 200, signal?: AbortSignal): Promise<HistoryResponse> {
  return request<HistoryResponse>(`/history?limit=${limit}`, { signal })
}

export interface LogResponse {
  lines: string[]
}

/** Хвост лог-файла (последние строки). */
export function getLog(limit = 200, signal?: AbortSignal): Promise<LogResponse> {
  return request<LogResponse>(`/log?limit=${limit}`, { signal })
}

export interface CommandAck {
  accepted: boolean
  id: string
  kind: string
}

/** Поставить идемпотентную команду в очередь движка. `id` — для дедупа (§1.9). */
export function postCommand(
  id: string,
  kind: CommandKind,
  payload: Record<string, unknown> = {},
): Promise<CommandAck> {
  return request<CommandAck>('/commands', { method: 'POST', body: { id, kind, payload } })
}

export interface WsTicketResponse {
  ticket: string
}

export function issueWsTicket(signal?: AbortSignal): Promise<WsTicketResponse> {
  return request<WsTicketResponse>('/ws/ticket', { method: 'POST', signal })
}

/** Сгенерировать уникальный id команды (идемпотентность на стороне движка). */
export function newCommandId(prefix: string): string {
  const rnd =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2)
  return `${prefix}-${rnd}`
}
